import React, { useEffect } from "react";
import {
  Modal,
  Form,
  Button,
  List,
  notification,
  Divider,
  Skeleton,
  Tag,
  Select,
} from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../hooks/useAuth";
import {
  fetchTask,
  updateTask,
  postTaskResponse,
} from "../services/tasksAPI";
import { formatDate, formatDateTimeLocal } from "../utils/dateUtils";
import { sanitizeHtml } from "../utils/textUtils";
import AttachmentsView from "./AttachmentsView";
import AttachmentsSection from "./AttachmentsSection";
import SimpleRichTextEditor from "./SimpleRichTextEditor";
import ProjectSettingToggle from "./ProjectSettingToggle";

const MANAGE_ROLES = ["owner", "project_manager", "field"];

const ViewTaskModal = ({ visible, onClose, taskId }) => {
  const { currentUser, selectedProject } = useAuth();
  const projectId = selectedProject?.project_id;
  const queryClient = useQueryClient();
  const [form] = Form.useForm();

  const { data: task, refetch } = useQuery({
    queryKey: ["task", taskId],
    queryFn: () => fetchTask(taskId),
    enabled: !!taskId,
  });

  const userRole = selectedProject?.role;
  const canManage = MANAGE_ROLES.includes(userRole);

  const currentUserId = String(currentUser?.id);
  const isAssignee = task?.assignees?.some(
    (a) => String(a.id || a._id) === currentUserId,
  );
  const isInBallInCourt = task?.ball_in_court_ids?.some(
    (uid) => String(uid) === currentUserId,
  );
  const isTaskManager =
    String(task?.task_manager?.id || task?.task_manager?._id || task?.task_manager_id) ===
    currentUserId;
  const canRespond = canManage || isAssignee || isTaskManager || isInBallInCourt;
  const showTransferBallSwitch =
    isAssignee && isInBallInCourt && !isTaskManager && task?.status === "Open";

  useEffect(() => {
    if (visible) refetch();
  }, [visible, refetch]);

  useEffect(() => {
    form.setFieldsValue({ transfer_ball_to_task_manager: false });
  }, [visible, form]);

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }) => updateTask(id, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["task", taskId] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      notification.success({ message: "Status updated successfully" });
    },
    onError: (error) => {
      notification.error({
        message: "Failed to update status",
        description: error.response?.data?.detail || error.message,
      });
    },
  });

  const returnBallMutation = useMutation({
    mutationFn: ({ id, ballInCourtIds }) =>
      updateTask(id, { ball_in_court_ids: ballInCourtIds }, true),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["task", taskId] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      notification.success({ message: "Ball in court returned to assignees" });
    },
    onError: (error) => {
      notification.error({
        message: "Failed to update ball in court",
        description: error.response?.data?.detail || error.message,
      });
    },
  });

  const responseMutation = useMutation({
    mutationFn: ({ id, response }) => postTaskResponse(id, response),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["task", taskId] });
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      form.resetFields(["response", "attachments"]);
      form.setFieldsValue({ transfer_ball_to_task_manager: false });
      notification.success({ message: "Response added successfully" });
    },
    onError: (error) => {
      notification.error({
        message: "Failed to add response",
        description: error.response?.data?.detail || error.message,
      });
    },
  });

  const handleAddResponse = (values) => {
    const payload = {
      response: values.response,
      attachments: values.attachments || [],
    };
    if (showTransferBallSwitch) {
      payload.transfer_ball_to_task_manager = values.transfer_ball_to_task_manager;
    }
    responseMutation.mutate({ id: taskId, response: payload });
  };

  const handleReturnBallToAssignees = () => {
    const assigneeIds =
      task?.assignee_ids || task?.assignees?.map((a) => a.id || a._id) || [];
    returnBallMutation.mutate({ id: task.id, ballInCourtIds: assigneeIds });
  };

  const ballIsWithManager =
    task?.ball_in_court_ids?.length === 1 &&
    String(task.ball_in_court_ids[0]) ===
      String(task?.task_manager_id || task?.task_manager?.id);

  const showManagerReturnBallButton =
    (canManage || isTaskManager) && ballIsWithManager && task?.status === "Open";

  const statusColor = { Open: "orange", Closed: "default", Draft: "blue" };
  const priorityColor = { High: "red", Medium: "gold", Low: "green" };

  const formatUserName = (user) =>
    user?.username || `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || "—";

  return (
    <Modal title="View Task" open={visible} onCancel={onClose} footer={null} width={800}>
      {task ? (
        <>
          <h2>
            #{task.number} — {task.title}
          </h2>

          <div className="mb-4">
            <strong>Task Detail:</strong>
            <div
              className="mt-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(task.task_detail) }}
            />
          </div>

          <p>
            <strong>Task Manager:</strong> {formatUserName(task.task_manager)}
          </p>

          <p>
            <strong>Assignees:</strong>{" "}
            {task.assignees?.map((a) => formatUserName(a)).join(", ")}
          </p>

          <p>
            <strong>Ball in Court:</strong>{" "}
            {task.ball_in_court_users?.map((u) => formatUserName(u)).join(", ") || "—"}
          </p>

          {task.distribution_list_details?.length > 0 && (
            <p>
              <strong>Distribution List:</strong>{" "}
              {task.distribution_list_details.map((u) => formatUserName(u)).join(", ")}
            </p>
          )}

          <div className="flex flex-wrap gap-4 my-3">
            <p className="flex flex-wrap items-center gap-2 m-0">
              <strong>Status:</strong>
              {canManage ? (
                <Select
                  value={task.status}
                  onChange={(val) => updateStatusMutation.mutate({ id: task.id, status: val })}
                  className="status-select-tag w-[120px]"
                  loading={updateStatusMutation.isPending}
                  disabled={updateStatusMutation.isPending}
                  popupRender={(menu) => menu}
                  suffixIcon={<span className="text-xs text-gray-400 ml-1">▼</span>}
                >
                  <Select.Option value="Open">
                    <Tag color="orange" className="m-0 w-full text-center block">
                      Open
                    </Tag>
                  </Select.Option>
                  <Select.Option value="Closed">
                    <Tag color="default" className="m-0 w-full text-center block">
                      Closed
                    </Tag>
                  </Select.Option>
                  <Select.Option value="Draft">
                    <Tag color="blue" className="m-0 w-full text-center block">
                      Draft
                    </Tag>
                  </Select.Option>
                </Select>
              ) : (
                <Tag color={statusColor[task.status]} className="m-0">
                  {task.status}
                </Tag>
              )}
            </p>
            <p className="m-0">
              <strong>Priority: </strong>
              <Tag color={priorityColor[task.priority]}>{task.priority}</Tag>
            </p>
            {task.due_date && (
              <p className="m-0">
                <strong>Due Date: </strong>
                {formatDate(task.due_date)}
              </p>
            )}
            {task.closed_at_date && (
              <p className="m-0">
                <strong>Closed Date: </strong>
                {formatDate(task.closed_at_date)}
              </p>
            )}
            {task.reminder_frequency && task.reminder_frequency !== "None" && (
              <p className="m-0">
                <strong>Reminder: </strong>
                {task.reminder_frequency}
              </p>
            )}
          </div>

          {task.attachments?.length > 0 && (
            <>
              <Divider />
              <div>
                <strong>Task Attachments:</strong>
                <div className="mt-2">
                  <AttachmentsView attachments={task.attachments} canDelete={false} />
                </div>
              </div>
            </>
          )}

          <Divider />
          <h3>Responses</h3>
          <List
            bordered
            dataSource={task.responses || []}
            locale={{ emptyText: "No responses yet" }}
            renderItem={(item) => (
              <List.Item>
                <div className="w-full">
                  <div className="mb-2">
                    <strong>{formatUserName(item.responded_by)}:</strong>
                    <div
                      className="mt-1 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.response) }}
                    />
                  </div>
                  {item.attachments?.length > 0 && (
                    <AttachmentsView attachments={item.attachments} canDelete={false} />
                  )}
                  <p className="text-gray-500 text-sm mt-2">
                    {formatDateTimeLocal(item.responded_on)}
                  </p>
                </div>
              </List.Item>
            )}
          />

          {showManagerReturnBallButton && (
            <Button
              type="default"
              className="mt-4"
              onClick={handleReturnBallToAssignees}
              loading={returnBallMutation.isPending}
            >
              Change Ball in Court to Assignees
            </Button>
          )}

          {canRespond && task.status === "Open" && (
            <Form
              form={form}
              onFinish={handleAddResponse}
              layout="vertical"
              className="mt-4"
              initialValues={{ transfer_ball_to_task_manager: false }}
            >
              <Form.Item
                label="Add Response"
                name="response"
                rules={[{ required: true, message: "Please input your response!" }]}
              >
                <SimpleRichTextEditor
                  placeholder="Enter your response..."
                  aiEnhanceTarget="response"
                />
              </Form.Item>

              <Form.Item
                name="attachments"
                label="Attachments"
                rules={
                  task.require_attachment_for_responses
                    ? [
                        {
                          validator: (_, value) =>
                            Array.isArray(value) && value.length > 0
                              ? Promise.resolve()
                              : Promise.reject(
                                  new Error("At least one attachment is required"),
                                ),
                        },
                      ]
                    : []
                }
              >
                <AttachmentsSection projectId={projectId} />
              </Form.Item>

              {showTransferBallSwitch && (
                <ProjectSettingToggle
                  name="transfer_ball_to_task_manager"
                  title="Change Ball in Court to Task Manager"
                />
              )}

              <Form.Item>
                <Button type="primary" htmlType="submit" loading={responseMutation.isPending}>
                  Submit Response
                </Button>
              </Form.Item>
            </Form>
          )}
        </>
      ) : (
        <Skeleton active />
      )}
    </Modal>
  );
};

export default ViewTaskModal;
